import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import {
  CheckIcon,
  ChevronDownIcon,
  EyeIcon,
  MailIcon,
  Trash2Icon,
} from "@lucide/icons";

import type { OpenAPI } from "simplydo/interfaces";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { Table, TableCell, TableRow } from "simplydo/react/ui/table";

import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import BaseChip from "components/lib/Chips/BaseChip";
import type { ChipUser } from "components/lib/Chips/types";
import { SelectIdeaChip } from "components/lib/Ideas/IdeaView/IdeaViewStyles";
import { FakeLink } from "components/lib/UI";

import type { AssignableUser } from "./AssignmentDashboard";

type AssessorRowProps = {
  item: AssignableUser;
  localAssignments:
    | {
        [key: string]: {
          idea: OpenAPI.Schemas["Idea"];
          assessmentRoundIds: string[];
        };
      }
    | undefined;
  onInspect: (user: AssignableUser) => void;
  isSelected: boolean;
  onSelect: () => void;
  removeAssessor: (id: string) => void;
};

export const AssessorRow = ({
  item,
  localAssignments,
  onInspect,
  isSelected,
  onSelect,
  removeAssessor,
}: AssessorRowProps) => {
  const { t } = useTranslation();
  const [assignmentIdeaCount, assignmentCount] = useMemo(() => {
    let ideaCount = 0;
    let count = 0;
    for (const assignment of Object.values(item.assignments || {})) {
      ideaCount += 1;
      count += assignment.assessmentRoundIds.length;
    }
    return [ideaCount, count];
  }, [item.assignments]);
  const [localIdeaCount, localCount] = useMemo(() => {
    let ideaCount = 0;
    let count = 0;
    for (const assignment of Object.values(localAssignments || {})) {
      ideaCount += 1;
      count += assignment.assessmentRoundIds.length;
    }
    return [ideaCount, count];
  }, [localAssignments]);

  return (
    <TableRow key={item._id}>
      <TableCell style={{ width: 30 }}>
        <SelectIdeaChip
          $compact
          $isSelected={isSelected}
          onClick={() => {
            onSelect();
          }}
        >
          <Icon icon={CheckIcon} />
        </SelectIdeaChip>
      </TableCell>
      <TableCell>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            marginTop: 2,
          }}
        >
          {item.isEmailInvitee ? (
            <BaseChip
              icon={MailIcon}
              title={item.email}
              compact="very"
              content={`This is a pending invitation, the user has not yet signed up to ${util.appName()}.`}
            />
          ) : (
            <UserChip user={item.ownerAssessor as ChipUser} compact="very" />
          )}
          {assignmentCount + localCount > 0 ? (
            <FakeLink
              style={{ fontSize: 12, lineHeight: "14px" }}
              onClick={() => {
                onInspect(item);
              }}
            >
              {util.pluralise(assignmentCount, "assessment", "assessments")}
              {assignmentCount > 0
                ? ` (in
            ${util.pluralise(assignmentIdeaCount, t("generic.idea"), t("generic.ideas"))})`
                : ""}
              {localCount > 0
                ? `, ${util.pluralise(localCount, "draft assignment", "draft assignments")}`
                : ""}
              {localCount > 0
                ? ` (in
            ${util.pluralise(localIdeaCount, t("generic.idea"), t("generic.ideas"))})`
                : ""}
            </FakeLink>
          ) : null}
        </div>
      </TableCell>
      <TableCell style={{ width: 40, overflow: "visible" }} collapsing>
        <Dropdown trigger={<Icon icon={ChevronDownIcon} />}>
          <DropdownMenu>
            <DropdownItem
              key="inspect"
              onClick={() => {
                onInspect(item);
              }}
            >
              <Icon icon={EyeIcon} /> View assignments
            </DropdownItem>
            {assignmentCount + localCount !== 0 ? null : (
              <DropdownItem
                key="remove"
                onClick={() => {
                  removeAssessor(item._id);
                }}
              >
                <Icon icon={Trash2Icon} /> Remove from list
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </TableCell>
    </TableRow>
  );
};
